import { Controller, FormProvider } from "react-hook-form";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";

import { FormField } from "components/form/FormField/FormField";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea";

import styles from "./AllergyFormModal.module.css";
import { useAllergyForm } from "./hooks/useAllergyForm";
import { SYMPTOM_OPTIONS } from "./utils/constants";

export const AllergyFormModal = ({
  isOpen,
  editId,
  data,
  patientId,
  onClose,
}) => {
  const {
    form,
    isItemDisabled,
    drugSearch,
    setDrugSearch,
    drugSearchResults,
    selectedDrug,
    handleDrugSelect,
    handleClearDrug,
    handleClose,
    handleSubmit,
    isPending,
  } = useAllergyForm({ isOpen, editId, data, patientId, onClose });

  const activeTab = form.watch("activeTab");
  const note = form.watch("note");
  const drugNameValue = form.watch("drugName");
  const drugGenericNameValue = form.watch("drugGenericName");

  return (
    <Dialog width={560} title="Allergies" onClose={handleClose} open={isOpen}>
      <FormProvider {...form}>
        <div className={styles.modalContent}>
          {isItemDisabled && (
            <div className={styles.disabledBanner}>
              This allergy has been deleted. All fields are read-only.
            </div>
          )}

          <div className={styles.tabContainer}>
            <Controller
              name="activeTab"
              control={form.control}
              render={({ field }) => (
                <ToggleButtonGroup
                  value={field.value}
                  exclusive
                  onChange={
                    isItemDisabled
                      ? undefined
                      : (_, newTab) => {
                          if (newTab) field.onChange(newTab);
                        }
                  }
                  aria-label="allergy type navigation"
                  className={styles.toggleBtnGroup}
                >
                  <ToggleButton
                    value="ALLERGY"
                    aria-label="Allergy"
                    className={styles.toggleBtn}
                    disableRipple
                    disabled={isItemDisabled}
                  >
                    Allergy
                  </ToggleButton>
                  <ToggleButton
                    value="DRUG_ALLERGY"
                    aria-label="Drug Allergy"
                    className={styles.toggleBtn}
                    disableRipple
                    disabled={isItemDisabled}
                  >
                    Drug Allergy
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />
          </div>

          <form onSubmit={handleSubmit} style={{ margin: 0, marginBottom: 1 }}>
            {activeTab === "ALLERGY" && (
              <FormField name="name" label="Name">
                <CustomInput
                  {...form.register("name")}
                  disabled={isItemDisabled}
                />
              </FormField>
            )}

            {activeTab === "DRUG_ALLERGY" && (
              <>
                <FormField name="drugSearch" label="Search Drug (Optional)">
                  <div className={styles.drugSearchContainer}>
                    <CustomInput
                      type="text"
                      value={drugSearch}
                      onChange={(e) => setDrugSearch(e.target.value)}
                      placeholder="Search for a drug to auto-fill fields..."
                      disabled={isItemDisabled}
                    />
                    {!isItemDisabled &&
                      drugSearchResults &&
                      drugSearchResults.length > 0 &&
                      drugSearch.length >= 3 &&
                      !selectedDrug && (
                        <div className={styles.drugSearchResults}>
                          {drugSearchResults.map((drug) => (
                            <div
                              key={drug.id}
                              className={styles.drugSearchItem}
                              onClick={() => handleDrugSelect(drug)}
                            >
                              <div className={styles.drugName}>
                                {drug.tradeName}
                              </div>
                              <div className={styles.drugGeneric}>
                                {drug.genericName}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </FormField>

                {!!selectedDrug && (
                  <div className={styles.selectedDrug}>
                    <div className={styles.selectedDrugName}>
                      Selected: {drugNameValue}
                    </div>
                    <div className={styles.selectedDrugGeneric}>
                      Generic: {drugGenericNameValue}
                    </div>
                    {!isItemDisabled && (
                      <Button
                        variant="text"
                        size="small"
                        className={styles.clearSelectionBtn}
                        onClick={handleClearDrug}
                      >
                        Clear selection
                      </Button>
                    )}
                  </div>
                )}

                <FormField name="drugName" label="Drug Name">
                  <CustomInput
                    {...form.register("drugName")}
                    placeholder="Enter drug name"
                    disabled={isItemDisabled}
                  />
                </FormField>
                <FormField name="drugGenericName" label="Generic Name">
                  <CustomInput
                    {...form.register("drugGenericName")}
                    placeholder="Enter generic name"
                    disabled={isItemDisabled}
                  />
                </FormField>
                <FormField name="ingredients" label="Ingredients">
                  <CustomInput
                    {...form.register("ingredients")}
                    placeholder="Enter ingredients"
                    disabled={isItemDisabled}
                  />
                </FormField>
              </>
            )}

            <FormField name="allergyType" label="Allergy Type">
              <CustomInput type="text" value="Allergy" disabled />
            </FormField>

            <FormField name="allergyReaction" label="Symptoms">
              <Controller
                name="allergyReaction"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={SYMPTOM_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isItemDisabled}
                    fullWidth
                  />
                )}
              />
            </FormField>

            {note !== null && (
              <FormField name="note" label="Note">
                <Controller
                  name="note"
                  control={form.control}
                  render={({ field }) => (
                    <Textarea
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      rows={3}
                      disabled={isItemDisabled}
                    />
                  )}
                />
              </FormField>
            )}

            <div className={styles.inputWrapperCheckbox}>
              <Controller
                name="serious"
                control={form.control}
                render={({ field }) => (
                  <CustomCheckbox
                    checked={field.value ?? false}
                    handleChange={(e) => field.onChange(e.target.checked)}
                    disabled={isItemDisabled}
                  />
                )}
              />
              <label className={styles.checkboxLabel}>Serious</label>
            </div>
          </form>
        </div>
      </FormProvider>
      <DialogFooter>
        <Button variant="outline" onClick={handleClose}>
          {isItemDisabled ? "Close" : "Cancel"}
        </Button>
        {!isItemDisabled && (
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Saving..." : "Save"}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};
